/**
 * The Marketing tab. Social presence and the venue QR code sit on the left,
 * the co-marketing panel and the two not-yet-built panels on the right. The
 * columns stack on a narrow screen. Whether anything can be edited comes from
 * the server's `canWriteMarketing` on the venue, never worked out here.
 *
 * Differences from the design are listed in the docblock of the component they
 * belong to (social presence, co-marketing panel, QR card, reserved panel). On
 * the tab itself: the two columns scroll with the page rather than each
 * scrolling alone, except the notes history, which scrolls in its own box on a
 * wide screen.
 */
import type { VenueDetail } from '../../../../api/venues';
import { MOBILE_MAX, useMediaQuery } from '../../../../lib/useMediaQuery';

import { CoMarketingPanel } from './CoMarketingPanel';
import { ReservedPanel } from './ReservedPanel';
import { SocialPresenceSection } from './SocialPresenceSection';
import { VenueQrCard } from './VenueQrCard';

const PHONE_MAX = '(max-width: 640px)';

export interface VenueMarketingTabProps {
  venue: VenueDetail;
}

export function VenueMarketingTab({ venue }: VenueMarketingTabProps) {
  const stacked = useMediaQuery(MOBILE_MAX);
  const phone = useMediaQuery(PHONE_MAX);
  return (
    <div
      style={{
        flex: 1,
        minHeight: 0,
        display: 'grid',
        gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : 'minmax(0, 1.15fr) minmax(0, 1fr)',
        gap: 14,
        alignItems: 'start',
        alignContent: 'start',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <section className="nb-card" style={{ minHeight: 0 }}>
          <SocialPresenceSection venue={venue} canEdit={venue.canWriteMarketing} compact={phone} />
        </section>
        <VenueQrCard compact={phone} />
      </div>
      <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}>
        <CoMarketingPanel venue={venue} stacked={stacked} />
        <ReservedPanel
          title="Promotion compliance"
          note="Promotion tracking is coming soon. It will hold the promotion state, notes and confirmation history set by the regional director."
        />
        <ReservedPanel
          title="Asset profile"
          note="Brand assets are coming soon. Logo, brand colours and generated kit assets for this venue will live here."
        />
      </div>
    </div>
  );
}
